import { Box, Button, Grid } from '@mui/material'
import dayjs from 'dayjs'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser'
import { useNotificationStack } from '../../../app/hooks/useNotificationStack'
import {
  useAcceptOrderByTenantIdMutation,
  useRecallOrderMutation,
  useRejectOrderByTenantIdMutation,
  useResetReviewOrderByTenantIdMutation,
  useReviewOrderByTenantIdMutation,
  useSubmitOrderMutation,
} from '../../../app/redux-fetch/apiOrders'
import CustomAlert from '../../../components/Alerts/CustomAlert'
import TooltipButton from '../../../components/Buttons/TooltipButton'
import ConditionalRender from '../../../components/ConditionalRender/ConditionalRender'
import { useConfirmDialogContext } from '../../../components/ConfirmDialog'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import ActionButton from '../../../components/Forms/ActionButton'
import rootEnum from '../../../components/Routes/rootEnum'
import TabWrapper from '../../../components/TabWrapper/TabWrapper'
import { OrderStatuses } from '../../../types/common'
import Note from '../../Notes/Note'
import TerminalInfo from '../../Shipments/components/TerminalInfo/TerminalInfo'
import Timeline from '../../Shipments/components/Timeline/Timeline'
import ViewShipmentTabHeader from '../../Shipments/components/ViewShipment/ViewShipmentTabHeader'
import TemperatureChart from '../../TemperatureChart/TemperatureChart'
import { OrderFormProvider } from '../context/OrderFormContext'
import messages from './messages'
import { IShipmentFormInput } from './schema'
import ShipmentSummary from './ShipmentSummary'
import { mapToShipmentFormInput } from './utils'
import { validateShipmentData, ValidationError } from './utils/crossValidation'

interface ViewShipmentProps {
  order: any
}

const ViewShipment = ({ order }: ViewShipmentProps): ReactElement => {

  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const { id } = useParams()
  const shipmentId = Number(id)
  const { tenantId, loggedInUser, isTenantUser, isCustomerUser } = useLoggedInUser()
  const loggedInUserId = loggedInUser.id

  const [currentPage, setCurrentPage] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUnderReview, setIsUnderReview] = useState<boolean>(false)
  const [countdown, setCountdown] = useState<number>(0)
  const [underReviewUserId, setUnderReviewUserId] = useState<number | undefined>()
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const { enqueueSuccess, enqueueFailure } = useNotificationStack()
  const { confirm } = useConfirmDialogContext()

  const [submitOrder] = useSubmitOrderMutation()
  const [acceptOrder] = useAcceptOrderByTenantIdMutation()
  const [rejectOrder] = useRejectOrderByTenantIdMutation()
  const [recallOrder] = useRecallOrderMutation()
  const [reviewOrder] = useReviewOrderByTenantIdMutation()
  const [resetReviewOrder] = useResetReviewOrderByTenantIdMutation()

  const methods = useForm({
    defaultValues: mapToShipmentFormInput(order),
    mode: 'all',
  })

  const { reset } = methods

  useEffect(() => {
    if (order) {
      const shipmentData = mapToShipmentFormInput(order)
      reset(shipmentData)

      const validateData = async () => {
        if (shipmentData.tenantId && shipmentData.customerId && shipmentData.bookings) {
          const errors = await validateShipmentData(shipmentData as IShipmentFormInput)
          setValidationErrors(errors)
        } else {
          setValidationErrors([])
        }
      }
      validateData()
    }
  }, [order, reset])

  useEffect(() => {
    if (order) {
      if (order.underReviewUntil && order.underReviewByUserId) {
        const utcDate = dayjs.utc(order.underReviewUntil)
        const diffTime = Math.max(0, Math.floor((utcDate.valueOf() - Date.now()) / 1000))
        if (diffTime > 0) {
          setCountdown(diffTime)
          setIsUnderReview(true)
          setUnderReviewUserId(order.underReviewByUserId)
        } else {
          setIsUnderReview(false)
          setUnderReviewUserId(undefined)
          setCountdown(0)
        }
      } else {
        setIsUnderReview(false)
        setUnderReviewUserId(undefined)
        setCountdown(0)
      }
    }
  }, [order])

  const isUnderReviewByCurrentUser = useCallback(() => {
    return isUnderReview && underReviewUserId === loggedInUserId
  }, [isUnderReview, underReviewUserId, loggedInUserId])

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)

      return () => clearTimeout(timer)
    } else if (isUnderReview && underReviewUserId === loggedInUserId) {
      handleResetReview()
    }
  }, [countdown, isUnderReview, underReviewUserId, loggedInUserId])

  const handleLockForReview = useCallback(async () => {
    try {
      setIsSubmitting(true)
      await reviewOrder({ tenantId, id: shipmentId })
      setUnderReviewUserId(loggedInUserId)
      setIsUnderReview(true)
      setCountdown(10 * 60)
      enqueueSuccess(messages.shipmentLockedForReview)
    } catch (e) {
      enqueueFailure(messages.shipmentUpdateError)
    } finally {
      setIsSubmitting(false)
    }
  }, [tenantId, order, shipmentId, loggedInUserId, formatMessage])

  const handleResetReview = useCallback(async () => {
    try {
      await resetReviewOrder({ tenantId, id: shipmentId })
      setIsUnderReview(false)
      setUnderReviewUserId(undefined)
      setCountdown(0)
    } catch (e) {
      enqueueFailure(messages.shipmentUpdateError)
    }
  }, [tenantId, order, shipmentId, formatMessage])

  const handleBackToShipment = useCallback(async () => {
    if (underReviewUserId === loggedInUserId && isUnderReview) {
      await handleResetReview()
    }
    navigate(rootEnum.SHIPMENTS)
  }, [navigate, underReviewUserId, loggedInUserId, isUnderReview, handleResetReview])

  const isUnderReviewOrDifferentUser = useCallback(() => {
    return isUnderReview && underReviewUserId !== loggedInUserId
  }, [isUnderReview, underReviewUserId, loggedInUserId])

  const isSubmitted =
    order.status === OrderStatuses.Submitted || order.status === OrderStatuses.UnderReview
  const isAccepted = order.status === OrderStatuses.Accepted

  const handleAccept = async () => {
    const shipmentData = mapToShipmentFormInput(order)
    if (shipmentData.tenantId && shipmentData.customerId && shipmentData.bookings) {
      const validationErrors = await validateShipmentData(shipmentData as IShipmentFormInput)

      if (validationErrors.length > 0) {
        enqueueFailure(messages.validationErrorsBeforeAccept)
        return
      }
    }

    const confirmed = await confirm({
      title: 'Accept Shipment',
      message: 'Are you sure you want to accept this shipment?',
      confirmText: 'Accept',
      cancelText: 'Cancel',
      severity: 'info',
      confirmButtonColor: 'success',
      cancelButtonColor: 'secondary',
    })

    if (confirmed) {
      try {
        if (!isSubmitted) {
          await submitOrder({ tenantId, id: order.id }).unwrap()
        }
        await acceptOrder({ tenantId, id: order.id }).unwrap()
        enqueueSuccess(messages.shipmentAccepted)
        navigate('/shipping/shipments')
      } catch (error) {
        enqueueFailure(messages.shipmentAcceptFailed)
      }
    }
  }

  const handleSubmit = async () => {
    const shipmentData = mapToShipmentFormInput(order)
    if (shipmentData.tenantId && shipmentData.customerId && shipmentData.bookings) {
      const validationErrors = await validateShipmentData(shipmentData as IShipmentFormInput)

      if (validationErrors.length > 0) {
        enqueueFailure(messages.validationErrorsBeforeSubmit)
        return
      }
    }

    const confirmed = await confirm({
      title: 'Submit Shipment',
      message: 'Are you sure you want to submit this shipment?',
      confirmText: 'Submit',
      cancelText: 'Cancel',
      severity: 'info',
      confirmButtonColor: 'success',
      cancelButtonColor: 'secondary',
    })

    if (confirmed) {
      try {
        await submitOrder({ tenantId, id: order.id }).unwrap()
        enqueueSuccess(messages.shipmentSubmitted)
        navigate('/shipping/shipments')
      } catch (error) {
        enqueueFailure(messages.shipmentSubmitFailed)
      }
    }
  }

  const handleReject = async () => {
    const confirmed = await confirm({
      title: 'Reject Shipment',
      message: 'Are you sure you want to reject this shipment?',
      confirmText: 'Reject',
      cancelText: 'Cancel',
      severity: 'warning',
      confirmButtonColor: 'error',
      cancelButtonColor: 'secondary',
    })

    if (confirmed) {
      try {
        await rejectOrder({ tenantId, id: order.id }).unwrap()
        enqueueSuccess(messages.shipmentRejected)
        navigate('/shipping/shipments')
      } catch (error) {
        enqueueFailure(messages.shipmentRejectFailed)
      }
    }
  }

  const handleRecall = async () => {
    const confirmed = await confirm({
      title: 'Withdraw Shipment',
      message: 'Are you sure you want to withdraw this shipment?',
      confirmText: 'Withdraw',
      cancelText: 'Cancel',
      severity: 'warning',
      confirmButtonColor: 'error',
      cancelButtonColor: 'secondary',
    })

    if (confirmed) {
      try {
        await recallOrder({ tenantId, id: order.id }).unwrap()
        enqueueSuccess(messages.shipmentRecalled)
        navigate('/shipping/shipments')
      } catch (error) {
        enqueueFailure(messages.shipmentRecallFailed)
      }
    }
  }

  const isConsignmentRequired = !order.bookings || order.bookings?.length === 0

  const isLoadRequired =
    !isConsignmentRequired &&
    order.bookings?.length > 0 &&
    !order.bookings.every(
      (booking: any) => booking?.loads && Array.isArray(booking.loads) && booking.loads.length > 0
    )

  const isLoadContentRequired =
    !isConsignmentRequired &&
    order.bookings?.length > 0 &&
    !order.bookings.every(
      (booking: any) =>
        booking?.loads &&
        Array.isArray(booking.loads) &&
        booking.loads.length > 0 &&
        booking.loads.some((pack: any) => {
          return (
            pack &&
            ((pack.value !== undefined && pack.quantity !== null) ||
              (Array.isArray(pack.contents) && pack.contents.length > 0))
          )
        })
    )

  const getTooltipMessage = () => {
    if (isConsignmentRequired) {
      return formatMessage(messages.noConsignment)
    }
    if (isLoadContentRequired && !isLoadRequired) {
      return formatMessage(messages.noLoadContent)
    }
    return formatMessage(messages.noLoad)
  }

  const isRejected = order.status === OrderStatuses.Rejected
  const isAbandoned = order.status === OrderStatuses.Abandoned

  return (
    <OrderFormProvider
      currentPage={currentPage}
      totalPages={4}
      setCurrentPage={setCurrentPage}
      isShipperEnabled={false}
      startPoint={undefined}
    >
      <FormProvider {...methods}>
        <DefaultSpacingGrid container spacing={1}>
          <DefaultSpacingGrid container spacing={1} pt={'8px'} mb={'2px'} pl={'10px'}>
            <Grid item xs>
              <Box display='flex' justifyContent='flex-start'>
                <Button color='primary' variant='outlined' onClick={handleBackToShipment}>
                  {formatMessage(messages.backToShipmentstBtn)}
                </Button>
                <ConditionalRender condition={isCustomerUser && isSubmitted && !isAccepted}>
                  <TooltipButton
                    variant='contained'
                    color='error'
                    onClick={handleRecall}
                    disabled={isUnderReview || isSubmitting}
                    sx={{ ml: '48px', whiteSpace: 'nowrap' }}
                  >
                    {formatMessage(messages.withdrawBtn)}
                  </TooltipButton>
                </ConditionalRender>
              </Box>
            </Grid>
            <Grid item>
              <Grid container justifyContent='flex-end' spacing={1}>
                <Grid item>
                  <ConditionalRender condition={isTenantUser && isSubmitted && !isAccepted}>
                    <ActionButton
                      variant='contained'
                      color='primary'
                      onClick={handleLockForReview}
                      sx={{ mr: '96px' }}
                      actionLabel={
                        isUnderReviewByCurrentUser() && countdown > 0 ? (
                          <Button
                            size='small'
                            onClick={async () => {
                              await handleResetReview()
                            }}
                            sx={{ textTransform: 'none', p: 0, mb: '4px' }}
                          >
                            {formatMessage(messages.unlock)}
                          </Button>
                        ) : (
                          <></>
                        )
                      }
                      disabled={isUnderReview || isSubmitting}
                    >
                      {countdown && countdown > 0
                        ? formatMessage(messages.countdownTitle, {
                          value: `[${dayjs()
                            .hour(0)
                            .minute(0)
                            .second(countdown)
                            .format('mm:ss')}]`,
                        })
                        : formatMessage(messages.lockForReview)}
                    </ActionButton>
                  </ConditionalRender>
                </Grid>
                <Grid item>
                  <ConditionalRender condition={isTenantUser && isSubmitted && !isAccepted}>
                    <Button
                      variant='contained'
                      color='error'
                      onClick={handleReject}
                      disabled={isUnderReviewOrDifferentUser() || isSubmitting}
                    >
                      {formatMessage(messages.buttonReject)}
                    </Button>
                  </ConditionalRender>
                </Grid>
                <Grid item>
                  <ConditionalRender condition={isTenantUser && isSubmitted}>
                    <TooltipButton
                      variant='contained'
                      color='success'
                      onClick={handleAccept}
                      disabled={
                        isUnderReviewOrDifferentUser() ||
                        isSubmitting ||
                        isConsignmentRequired ||
                        isLoadRequired ||
                        validationErrors.length > 0
                      }
                      showTooltip={isConsignmentRequired || isLoadRequired}
                      tooltipProps={{
                        placement: 'top',
                        title:
                          validationErrors.length > 0
                            ? formatMessage(messages.fixValidationErrors)
                            : getTooltipMessage(),
                        children: <></>,
                      }}
                    >
                      {formatMessage(messages.acceptShipment)}
                    </TooltipButton>
                  </ConditionalRender>
                </Grid>
                <Grid item>
                  <ConditionalRender
                    condition={!isSubmitted && !isAccepted && !isRejected && !isAbandoned}
                  >
                    <Box display='flex' justifyContent='flex-start'>
                      <TooltipButton
                        variant='contained'
                        onClick={handleSubmit}
                        disabled={
                          isUnderReview ||
                          isSubmitting ||
                          isConsignmentRequired ||
                          isLoadRequired ||
                          isAbandoned ||
                          validationErrors.length > 0
                        }
                        color='success'
                        showTooltip={
                          isConsignmentRequired || isLoadRequired || validationErrors.length > 0
                        }
                        tooltipProps={{
                          placement: 'top',
                          title:
                            validationErrors.length > 0
                              ? formatMessage(messages.fixValidationErrors)
                              : getTooltipMessage(),
                          children: <></>,
                        }}
                      >
                        {formatMessage(messages.submitShipment)}
                      </TooltipButton>
                    </Box>
                  </ConditionalRender>
                </Grid>
              </Grid>
            </Grid>
          </DefaultSpacingGrid>
          <Grid item xs={12} md={12}>
            {isConsignmentRequired && (
              <CustomAlert
                severity='error'
                title='Error'
                message={formatMessage(messages.noConsignment)}
              />
            )}
            {isLoadRequired && (
              <CustomAlert
                severity='error'
                title='Error'
                message={formatMessage(messages.noLoad)}
              />
            )}
            {isLoadContentRequired && !isLoadRequired && (
              <CustomAlert
                severity='error'
                title='Error'
                message={formatMessage(messages.noLoadContent)}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <ViewShipmentTabHeader />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ pt: '28px', pb: '28px', width: '100%' }}>
              <TabWrapper
                tabs={[
                  {
                    label: 'Details',
                    content: (
                      <Grid item xs={12}>
                        <ShipmentSummary data={mapToShipmentFormInput(order)} />
                      </Grid>
                    ),
                  },
                  {
                    label: 'Timeline',
                    content: (
                      <Grid item xs={12}>
                        <Timeline />
                      </Grid>
                    ),
                  },
                  {
                    label: 'Notes',
                    content: (
                      <Grid item xs={12}>
                        <Note />
                      </Grid>
                    ),
                  },
                  {
                    label: 'Temperature',
                    content: (
                      <Grid item xs={12}>
                        <TemperatureChart data={[]} loadId={0} loadName={''} title={''} />
                      </Grid>
                    ),
                  },
                  {
                    label: 'Terminal Info',
                    content: (
                      <Grid item xs={12}>
                        <TerminalInfo />
                      </Grid>
                    ),
                  },
                ]}
                ariaLabel='Shipment View Tabs'
                defaultTab={currentPage}
                onChange={setCurrentPage}
                tabAlignment='center'
              />
            </Box>
          </Grid>
        </DefaultSpacingGrid>
      </FormProvider>
    </OrderFormProvider>
  )
}

export default ViewShipment
